import { Button, Card, Col, Row } from 'react-bootstrap';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageTitle from '../../components/atoms/PageTitle/PageTitle';
import useLecture from '../../hooks/theory/useLecture';
import CommentsCard from '../../components/organisms/CommentsCard/CommentsCard';
import useModal from '../../hooks/useModal';
import { useUser } from '../../hooks';
import { SeoEntityType } from '../../types/cms/seoData';
import { SeoDetails } from '../../components/atoms/CMS/SeoDetails';
import classNames from 'classnames';
import { LectureRecap } from '../../types/theory/lecture';
// @ts-ignore
import TimeMe from 'timeme.js';
import Prism from 'prismjs';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-java';
import useTimespentLog from '../user/logs/hooks/useTimespentLog';

const LectureDetailsPage = () => {
    const { loggedInUserRoles } = useUser();
    const { lectureSlug } = useParams<{ lectureSlug: string }>();
    const { loading, userLoadLectureDetails, userLectureDetails, userLoadLectureRecap, userLectureRecap, error } = useLecture();
    const { isOpen, className, toggleModal, openModalWithClass } = useModal();

    const timestamp = Math.floor(new Date().getTime() / 1000);
    const { userCreateTimespent } = useTimespentLog();
    const [pageTimestamp, setPageTimestamp] = React.useState<number>(timestamp);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const sendTimeMeData = () => {
        const timeSpentOnPage = TimeMe.getTimeOnPageInSeconds(`lecture-${lectureSlug}`);
        userCreateTimespent({
            amount: Math.floor(timeSpentOnPage),
            timestamp: pageTimestamp,
            domain: 'lecture',
            lectureSlug: lectureSlug,
        });
    };

    useEffect(() => {
        if (userLectureDetails) {
            Prism.highlightAll();
        }
    }, [userLectureDetails]);

    useEffect(() => {
        userLoadLectureDetails(lectureSlug!!);
        userLoadLectureRecap(lectureSlug!!);
    }, [lectureSlug]);

    useEffect(() => {
        TimeMe.initialize({
            currentPageName: `lecture-${lectureSlug}`,
            idleTimeoutInSeconds: 3,
        });
        TimeMe.resetAllRecordedPageTimes();
        TimeMe.startTimer(`lecture-${lectureSlug}`);

        const handleUnload = (event: BeforeUnloadEvent) => {
            sendTimeMeData();
        };

        window.addEventListener('beforeunload', handleUnload);

        return () => {
            sendTimeMeData();
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, []);

    const handleQuizClick = () => {
        navigate('/lectures/' + lectureSlug + '/questions');
    };

    return (
        <>
            {userLectureDetails && (
                <>
                    <SeoDetails type={SeoEntityType.LECTURE} slug={lectureSlug ?? ''} />
                    <PageTitle
                        breadCrumbItems={[
                            { label: t('lecture.plural'), path: '/lecture', active: true },
                            { label: userLectureDetails.title, path: '/lectures/' + lectureSlug, active: true },
                        ]}
                        title={t('lecture.plural')}
                    />
                    <Row>
                        <hr className='hr hr-blurry' />
                    </Row>
                    {loggedInUserRoles.includes('ADMIN') && (
                        <Row>
                            <Col>
                                <Card>
                                    <Card.Header className='py-2 bg-danger'>
                                        <h4 className='header-title mb-0 text-white fw-bolder'>Admin Panel: Manage Lecture</h4>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col>
                                                <Link to={`/admin/theory/lectures/${userLectureDetails.id}`} target={'_blank'}>
                                                    <Button variant='primary' onClick={() => openModalWithClass('modal-full-width')}>
                                                        Edit lecture
                                                    </Button>
                                                </Link>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col>
                            <Card>
                                <Card.Header className='py-2 bg-primary'>
                                    <h4 className='header-title mb-0 text-white fw-bolder'>{userLectureDetails.title}</h4>
                                </Card.Header>
                                <Card.Body>
                                    <div dangerouslySetInnerHTML={{ __html: userLectureDetails.content }} />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    {userLectureDetails.canAnswerQuestions && (
                        <Row className={'mb-3'}>
                            <Col>
                                <Link to={`/lectures/${lectureSlug}/questions`} className={'btn btn-primary font-14 float-end'}>
                                    {t('lecture.quiz')}
                                    <i className='mdi mdi-arrow-right-bold ms-2'></i>
                                </Link>
                            </Col>
                        </Row>
                    )}
                    {userLectureRecap && (
                        <>
                            <Row>
                                <Col>
                                    <Card>
                                        <Card.Header className='py-2 bg-primary'>
                                            <h4 className='header-title mb-0 text-white fw-bolder'>{t('lecture.recap_questions')}</h4>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row className='justify-content-between'>
                                                <Col className='col-auto'>
                                                    <span className='fw-bold text-uppercase'>{t('lecture.recap_title')}</span>
                                                </Col>
                                                <Col className='col-auto mb-1'>
                                                    <Row>
                                                        <Col>
                                                            <span className='fw-bold text-uppercase'>{t('lecture.recap_solved')}</span>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                            {userLectureRecap.questions.map((question: LectureRecap, index: number) => (
                                                <Row className='justify-content-between mt-2' key={index}>
                                                    <Col className='col-auto'>
                                                        <span className='fw-bold font-16'>{question.title}</span>
                                                    </Col>
                                                    <Col className='col-auto'>
                                                        <Row>
                                                            <Col>
                                                                <div className='lecture-col me-3'>
                                                                    <span className={classNames('rounded-circle font-24', question.isSolved ? 'chapter-success' : 'chapter-progress')}>
                                                                        {question.isSolved ? <i className='mdi mdi-check-bold'></i> : <></>}
                                                                    </span>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                            ))}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                            {userLectureRecap.problems.length > 0 && (
                                <Row>
                                    <Col>
                                        <Card>
                                            <Card.Header className='py-2 bg-primary'>
                                                <h4 className='header-title mb-0 text-white fw-bolder'>{t('lecture.recap_problems')}</h4>
                                            </Card.Header>
                                            <Card.Body>
                                                <Row className='justify-content-between'>
                                                    <Col className='col-auto'>
                                                        <span className='fw-bold text-uppercase'>{t('lecture.recap_title')}</span>
                                                    </Col>
                                                    <Col className='col-auto mb-1'>
                                                        <Row>
                                                            <Col>
                                                                <span className='fw-bold text-uppercase'>{t('lecture.recap_solved')}</span>
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                                {userLectureRecap.problems.map((problem: LectureRecap, index: number) => (
                                                    <Row className='justify-content-between mt-2' key={index}>
                                                        <Col className='col-auto'>
                                                            <Link to={`/problems/${problem.slug}`} className={'fw-bold font-16'}>
                                                                <i className={'mdi mdi-link me-2'} />
                                                                {problem.title}
                                                            </Link>
                                                        </Col>
                                                        <Col className='col-auto'>
                                                            <Row>
                                                                <Col>
                                                                    <div className='lecture-col me-3'>
                                                                        <span className={classNames('rounded-circle font-24', problem.isSolved ? 'chapter-success' : 'chapter-progress')}>
                                                                            {problem.isSolved ? <i className='mdi mdi-check-bold'></i> : <></>}
                                                                        </span>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                ))}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}

                    <Row>
                        <Col>
                            <CommentsCard domain={'lectures'} domainId={userLectureDetails?.id} />
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
};

export default LectureDetailsPage;
